import React from 'react'
import { Box, Card, Chip, Button } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'

function ProjetsCreables({ projets, onCreate, onCreateObjectif, formatMoney }) {
  const { t } = useTranslation('layout')
  // formatMoney est injecté par la page (devise du pays de l'utilisateur).
  // Repli sur le formatage € local si non fourni (rétro-compat / tests).
  const money = formatMoney || formatEuro

  return (
    <Card
      sx={{
        background: '#0e1a36',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: 3,
        color: 'white',
      }}
    >
      <WTypography variant="overline" sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}>
        {t('conseillerPatrimonial.projets.overline')}
      </WTypography>
      <WTypography variant="h5" sx={{ fontWeight: 700, mt: 0.5, mb: 0.5 }}>
        {t('conseillerPatrimonial.projets.title')}
      </WTypography>
      <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mb: 3 }}>
        {t('conseillerPatrimonial.projets.subtitle')}
      </WTypography>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
        gap={2}
      >
        {projets.map((p) => (
          <ProjetCard
            key={p.id}
            projet={p}
            money={money}
            onCreate={() => onCreate?.(p)}
            onCreateObjectif={
              onCreateObjectif ? () => onCreateObjectif(p) : undefined
            }
            t={t}
          />
        ))}
      </Box>
    </Card>
  )
}

function ProjetCard({ projet, onCreate, onCreateObjectif, t, money = formatEuro }) {
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'all .25s ease',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: projet.color,
          boxShadow: `0 8px 24px ${projet.color}33`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, ${projet.color}18 0%, transparent 60%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" zIndex={1}>
        <Chip
          label={projet.pillar}
          size="small"
          sx={{
            bgcolor: `${projet.color}22`,
            color: projet.color,
            fontWeight: 600,
            height: 22,
            fontSize: 11,
          }}
        />
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '12px',
            bgcolor: `${projet.color}22`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: projet.color,
            fontSize: 20,
            fontWeight: 700,
          }}
        >
          {projet.title.charAt(0)}
        </Box>
      </Box>

      <Box zIndex={1}>
        <WTypography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>
          {projet.title}
        </WTypography>
        <WTypography
          variant="caption"
          sx={{ color: '#c1d0eb !important', display: 'block', mt: 0.5, lineHeight: 1.5 }}
        >
          {projet.description}
        </WTypography>
      </Box>

      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} zIndex={1}>
        <MetricCell label={t('conseillerPatrimonial.projets.target')} value={money(projet.targetAmount)} />
        <MetricCell
          label={t('conseillerPatrimonial.projets.monthly')}
          value={`${money(projet.monthlyContribution)}/${t('conseillerPatrimonial.projets.month')}`}
        />
        <MetricCell label={t('conseillerPatrimonial.projets.duration')} value={projet.duration} />
        <MetricCell label={t('conseillerPatrimonial.projets.yield')} value={projet.expectedYield} />
      </Box>

      <WTypography variant="caption" sx={{ color: '#a0b4d6 !important', zIndex: 1 }}>
        {t('conseillerPatrimonial.projets.vehicle')} :{' '}
        <strong style={{ color: '#dbe5f7' }}>{projet.vehicle}</strong>
      </WTypography>

      {/* LAP-fix-projet-vs-objectif — Deux CTAs distincts :
            • "Créer ce projet"   → ouvre CreateProject (MesToDo / entité Projet)
            • "Créer un objectif" → ouvre CreateObjectifsForm (MesObjectifs,
              avec accès aux objectifs prédéfinis du compte).
          Le clic sur la carte elle-même n'a plus de side-effect ambigu. */}
      <Box display="flex" flexDirection="column" gap={1} mt="auto" zIndex={1}>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={(e) => {
            e.stopPropagation()
            onCreate?.()
          }}
          sx={{
            bgcolor: projet.color,
            color: '#0a1429',
            fontWeight: 700,
            textTransform: 'none',
            py: 1,
            '&:hover': { bgcolor: projet.color, filter: 'brightness(1.1)' },
          }}
        >
          {t('conseillerPatrimonial.projets.createCta')}
        </Button>
        {onCreateObjectif && (
          <Button
            variant="outlined"
            startIcon={<FlagOutlinedIcon />}
            onClick={(e) => {
              e.stopPropagation()
              onCreateObjectif()
            }}
            sx={{
              color: projet.color,
              borderColor: `${projet.color}66`,
              fontWeight: 600,
              textTransform: 'none',
              py: 0.9,
              '&:hover': {
                borderColor: projet.color,
                bgcolor: `${projet.color}14`,
              },
            }}
          >
            {t('conseillerPatrimonial.projets.createObjectifCta')}
          </Button>
        )}
      </Box>
    </Box>
  )
}

function MetricCell({ label, value }) {
  return (
    <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, p: 1 }}>
      <WTypography variant="caption" sx={{ color: '#a0b4d6 !important', fontSize: 10, display: 'block' }}>
        {label}
      </WTypography>
      <WTypography variant="body2" sx={{ fontWeight: 600, color: '#fff !important', fontSize: 13 }}>
        {value}
      </WTypography>
    </Box>
  )
}

function formatEuro(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(0)}k €`
  return `${n}€`
}

export default ProjetsCreables
